import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { ArrowRight, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Van transfer: move everything in one location onto another in a single go.
 * Drivers swap vehicles far more often than tools change hands, so the
 * default is "take the lot" — untick the few items staying behind.
 */
const LocationTransferDialog = ({ open, onOpenChange, locations = [], sourceId = '', onDone }) => {
  const { t } = useTranslation();
  const [source, setSource] = useState(sourceId || '');
  const [target, setTarget] = useState('');
  const [rows, setRows] = useState([]);
  const [held, setHeld] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setSource(sourceId || ''); setTarget(''); setHeld(new Set()); } }, [open, sourceId]);

  const loadRows = useCallback(async () => {
    if (!source) { setRows([]); return; }
    setLoading(true);
    try {
      const params = source === 'unassigned' ? {} : { storage_location_id: source };
      const r = await axios.get(`${API}/inventory/assets`, { params });
      const list = source === 'unassigned' ? r.data.filter((a) => !a.storage_location_id) : r.data;
      setRows(list);
      setHeld(new Set());
    } catch { toast.error(t('inv.assets.loadFailed')); }
    setLoading(false);
  }, [source, t]);

  useEffect(() => { if (open) loadRows(); }, [open, loadRows]);

  const toggleHold = (id) => setHeld((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const moving = rows.filter((a) => !held.has(a.id));

  const submit = async () => {
    if (!source || !target || moving.length === 0) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/inventory/storage-locations/${source}/transfer`, {
        target_location_id: target === 'unassigned' ? null : target,
        asset_ids: moving.map((a) => a.id),
      });
      toast.success(t('inv.transfer.done', { count: r.data.moved }));
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || t('inv.transfer.failed')); }
    setSaving(false);
  };

  const nameOf = (id) => (id === 'unassigned'
    ? t('inv.assets.noLocation')
    : (locations.find((l) => l.id === id)?.name || '—'));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="location-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5" />{t('inv.transfer.title')}</DialogTitle>
          <DialogDescription>{t('inv.transfer.hint')}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-end">
          <div className="min-w-0">
            <label className="text-xs text-muted-foreground">{t('inv.transfer.from')}</label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger data-testid="transfer-source-select"><SelectValue placeholder={t('inv.form.storageLocation')} /></SelectTrigger>
              <SelectContent>
                {locations.map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                <SelectItem value="unassigned">{t('inv.assets.noLocation')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ArrowRight className="w-4 h-4 mb-2.5 mx-auto hidden sm:block text-muted-foreground" />
          <div className="min-w-0">
            <label className="text-xs text-muted-foreground">{t('inv.transfer.to')}</label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="transfer-target-select"><SelectValue placeholder={t('inv.form.storageLocation')} /></SelectTrigger>
              <SelectContent>
                {locations.filter((l) => l.id !== source).map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                {source !== 'unassigned' && <SelectItem value="unassigned">{t('inv.assets.noLocation')}</SelectItem>}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded border">
          <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-2 text-xs">
            <span data-testid="transfer-count">{t('inv.transfer.movingCount', { count: moving.length, total: rows.length })}</span>
            {rows.length > 0 && (
              <button type="button" className="underline underline-offset-2"
                onClick={() => setHeld(held.size ? new Set() : new Set(rows.map((a) => a.id)))}
                data-testid="transfer-toggle-all">
                {held.size ? t('inv.transfer.selectAll') : t('inv.transfer.clearAll')}
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto divide-y">
            {loading && <div className="p-4 text-center text-sm text-muted-foreground">{t('common.loading')}</div>}
            {!loading && rows.length === 0 && (
              <div className="p-4 text-center text-sm text-muted-foreground" data-testid="transfer-empty">
                {source ? t('inv.transfer.empty') : t('inv.transfer.pickSource')}
              </div>
            )}
            {rows.map((a) => (
              <label key={a.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/40"
                data-testid={`transfer-item-${a.inventory_number}`}>
                <Checkbox checked={!held.has(a.id)} onCheckedChange={() => toggleHold(a.id)} />
                <span className="font-mono text-xs text-muted-foreground w-24 shrink-0">{a.inventory_number}</span>
                <span className="break-words">{a.name}</span>
              </label>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="transfer-cancel-btn">{t('common.cancel')}</Button>
          <Button onClick={submit} disabled={!source || !target || moving.length === 0 || saving}
            data-testid="transfer-confirm-btn">
            {saving ? t('common.loading') : t('inv.transfer.move', { count: moving.length, to: nameOf(target) })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LocationTransferDialog;
